"use client"

import React, { memo, useState, useRef, useEffect, useLayoutEffect, useCallback, useId } from "react"
import { createPortal } from "react-dom"

import { pushLayer } from "@/lib/layers/layerStack"

import { useLayerOwner } from "@/lib/layers/LayerOwner"

/** The tooltip's own band when it has no layer owner (unchanged since before the layer work). */
const TOOLTIP_LEGACY_Z = 10100

/**
 * The z a body-portalled tooltip needs to show above the surface its trigger sits in (docs/THEME.md 2.10). Inside a
 * nested dialog (z 100101) or a hand-built modal (z 99999) a tooltip at 10100 opened BEHIND its own dialog. Returns the
 * highest stacking z among the trigger's positioned ancestors plus one, or null when 10100 is already above them (the
 * page: nothing changes there). Exported for tests.
 */
export function tooltipZAbove(trigger: Element | null): number | null {
  let highest = -Infinity
  for (let el = trigger?.parentElement ?? null; el; el = el.parentElement) {
    const style = window.getComputedStyle(el)
    if (style.position === "static") continue
    const z = Number.parseInt(style.zIndex, 10)
    if (Number.isFinite(z)) highest = Math.max(highest, z)
  }
  return highest >= TOOLTIP_LEGACY_Z ? highest + 1 : null
}

// ---------------------------------------------------------------------------
// Global tooltip coordination (single-tooltip-at-a-time)
//
// Problem (#40): Multiple InstantTooltip instances each manage their own
// visibility via independent useState hooks.  When the user moves quickly
// between two adjacent interactive tooltips (e.g. Info icon -> Completion
// Criteria icon), the previous tooltip's portal can persist at z-[9999]
// with pointer-events:auto, covering the viewport and blocking all
// interaction — requiring a full page reload to recover.
//
// Solution: A lightweight module-level event bus.  Every tooltip subscribes
// to a "dismiss" signal.  When any tooltip opens it broadcasts "dismiss"
// first, causing all *other* visible tooltips to close synchronously before
// the new one renders its portal.
//
// Additional safeguards:
//   - Scroll dismiss: any scroll event on the page dismisses all tooltips
//   - Escape key: pressing Escape dismisses the active tooltip
//   - Click-outside: clicking outside interactive tooltips dismisses them
//   - Safety timeout: interactive tooltips auto-dismiss after 15s max
// ---------------------------------------------------------------------------
type DismissListener = (senderId: number) => void
let _nextTooltipId = 0
const _dismissListeners = new Set<DismissListener>()

/** Max lifetime for interactive tooltips (ms). Prevents "stuck" tooltips.
 *  Set generously (15s) so it doesn't interfere with reading long content,
 *  but catches tooltips that escaped all other dismiss mechanisms. */
const INTERACTIVE_TOOLTIP_MAX_LIFETIME_MS = 15000

// ---------------------------------------------------------------------------
// Layer 2: Orphan detector — tracks active tooltips and polls for dead triggers
// ---------------------------------------------------------------------------
interface ActiveTooltipEntry {
  dismiss: () => void
  triggerRef: React.RefObject<HTMLSpanElement | null>
}
const _activeTooltips = new Map<number, ActiveTooltipEntry>()
let _orphanPollInterval: ReturnType<typeof setInterval> | null = null
const ORPHAN_POLL_MS = 200

/**
 * True when the trigger has collapsed to nothing the user could point at. The trigger span itself measures 0x0 when its
 * only content is out of flow (an `absolute` overlay icon inside a relative frame): that is a live trigger, not a dead
 * one, so the box counts as collapsed only when no child has a size either. Single definition for the poller and the
 * click safety valve.
 */
function _isCollapsedTrigger(trigger: HTMLElement): boolean {
  const isEmpty = (el: Element) => {
    const r = el.getBoundingClientRect()
    return r.width === 0 && r.height === 0
  }
  return isEmpty(trigger) && Array.from(trigger.children).every(isEmpty)
}

function _startOrphanPolling() {
  if (_orphanPollInterval !== null) return
  _orphanPollInterval = setInterval(() => {
    if (_activeTooltips.size === 0) {
      _stopOrphanPolling()
      return
    }
    _activeTooltips.forEach((entry, id) => {
      const trigger = entry.triggerRef.current
      if (!trigger || !document.body.contains(trigger)) {
        if (process.env.NODE_ENV !== 'production') {
          console.debug('[Tooltip] Orphan detected: trigger removed from DOM (id=%d)', id)
        }
        entry.dismiss()
        return
      }
      if (_isCollapsedTrigger(trigger)) {
        if (process.env.NODE_ENV !== 'production') {
          console.debug('[Tooltip] Orphan detected: trigger collapsed to zero dimensions (id=%d)', id)
        }
        entry.dismiss()
      }
    })
  }, ORPHAN_POLL_MS)
}

function _stopOrphanPolling() {
  if (_orphanPollInterval !== null) {
    clearInterval(_orphanPollInterval)
    _orphanPollInterval = null
  }
}

function _registerActiveTooltip(id: number, entry: ActiveTooltipEntry) {
  _activeTooltips.set(id, entry)
  _startOrphanPolling()
}

function _unregisterActiveTooltip(id: number) {
  _activeTooltips.delete(id)
  if (_activeTooltips.size === 0) {
    _stopOrphanPolling()
  }
}

// ---------------------------------------------------------------------------
// Layer 3: Global click safety valve — last-resort orphan cleanup on any click
// ---------------------------------------------------------------------------
let _safetyValveAttached = false
function _ensureSafetyValve() {
  if (_safetyValveAttached || typeof window === 'undefined') return
  _safetyValveAttached = true
  document.addEventListener('pointerdown', () => {
    // On any click, check for orphaned tooltips whose triggers are gone
    _activeTooltips.forEach((entry, id) => {
      const trigger = entry.triggerRef.current
      if (!trigger || !document.body.contains(trigger)) {
        if (process.env.NODE_ENV !== 'production') {
          console.debug('[Tooltip] Safety valve dismissed orphan on click (id=%d)', id)
        }
        entry.dismiss()
        return
      }
      if (_isCollapsedTrigger(trigger)) {
        if (process.env.NODE_ENV !== 'production') {
          console.debug('[Tooltip] Safety valve dismissed collapsed trigger on click (id=%d)', id)
        }
        entry.dismiss()
      }
    })
  }, true) // capture phase
}

// ---------------------------------------------------------------------------
// Broadcast dismiss coordination
// ---------------------------------------------------------------------------
function subscribeDismiss(listener: DismissListener) {
  _dismissListeners.add(listener)
  return () => { _dismissListeners.delete(listener) }
}

function broadcastDismiss(senderId: number) {
  _dismissListeners.forEach(fn => fn(senderId))
}

/** Dismiss ALL visible tooltips (used by scroll handler). */
function broadcastDismissAll() {
  _dismissListeners.forEach(fn => fn(-1))
}

// Global scroll listener — dismiss all tooltips on ANY scroll event.
// Registered once when the module loads (client-side only).
// Tooltips use position:fixed but their triggers scroll with the container,
// causing the tooltip to "float" detached from the trigger after scroll.
let _scrollListenerAttached = false
function ensureScrollListener() {
  if (_scrollListenerAttached || typeof window === 'undefined') return
  _scrollListenerAttached = true
  document.addEventListener('scroll', (event) => {
    // Scrolling the content of an interactive tooltip (a long answer) is reading, not a page scroll.
    if (event.target instanceof Element
      && event.target.closest('[data-instant-tooltip-scroll-portal="true"]')) return
    broadcastDismissAll()
  }, { capture: true, passive: true })
}

// ---------------------------------------------------------------------------
// Accessible name for icon-only children (WCAG 4.1.2)
// ---------------------------------------------------------------------------
const UNNAMED_CONTROL_SELECTOR =
  'button, a[href], [role="button"], [role="link"], [role="tab"], [role="menuitem"]'
const TOOLTIP_NAME_MARK = 'data-tooltip-name'

// Which tooltip instance named a control, so a tooltip wrapping another tooltip
// never renames (or strips) the name the inner one gave.
const tooltipNameOwner = new WeakMap<Element, object>()
// What can change a child's name on its own, without the tooltip re-rendering.
const NAME_OBSERVE_OPTIONS: MutationObserverInit = {
  childList: true,
  subtree: true,
  characterData: true,
  attributes: true,
  attributeFilter: ['aria-label', 'aria-labelledby', 'title', 'alt'],
}

/** A name from the control itself; `ownAriaLabel` false ignores its aria-label (the one we may have set). */
function hasAccessibleName(el: Element, ownAriaLabel = true): boolean {
  if (ownAriaLabel && el.getAttribute('aria-label')?.trim()) return true
  if (el.getAttribute('aria-labelledby')?.trim()) return true
  if (el.getAttribute('title')?.trim()) return true
  if (el.textContent?.trim()) return true
  return !!el.querySelector('img[alt]:not([alt=""]), svg title, [aria-label]:not([aria-label=""])')
}

function releaseTooltipName(el: Element) {
  // Remove only what we still own: a caller's own label must survive.
  if (el.getAttribute('aria-label') === el.getAttribute(TOOLTIP_NAME_MARK)) el.removeAttribute('aria-label')
  el.removeAttribute(TOOLTIP_NAME_MARK)
  tooltipNameOwner.delete(el)
}

/**
 * Give `text` as aria-label to every unnamed control under `root`. TOOLTIP_NAME_MARK
 * holds the exact text we set: if the caller later gives the control its own
 * aria-label (value no longer matches) or the control gains its own name (text,
 * title, labelled image), we step aside. Writes only on change, so it is safe to
 * call from the observer that its own writes wake.
 */
function syncTooltipNames(root: Element, text: string, owner: object) {
  root.querySelectorAll(UNNAMED_CONTROL_SELECTOR).forEach(el => {
    const ours = el.getAttribute(TOOLTIP_NAME_MARK)
    if (ours !== null) {
      const by = tooltipNameOwner.get(el)
      if (by && by !== owner) return
      if (el.getAttribute('aria-label') !== ours || hasAccessibleName(el, false)) {
        releaseTooltipName(el)
      } else if (ours !== text) {
        el.setAttribute('aria-label', text)
        el.setAttribute(TOOLTIP_NAME_MARK, text)
      }
      return
    }
    if (hasAccessibleName(el)) return
    el.setAttribute('aria-label', text)
    el.setAttribute(TOOLTIP_NAME_MARK, text)
    tooltipNameOwner.set(el, owner)
  })
}

interface InstantTooltipProps {
  content: React.ReactNode
  /** Opt-in keyboard trigger for explanatory chips without their own control. */
  focusable?: boolean
  children: React.ReactNode
  /** Position relative to trigger - top, bottom, left, or right */
  position?: 'top' | 'bottom' | 'left' | 'right'
  className?: string
  /** Tooltip glow intensity */
  glow?: 'default' | 'white'
  /** Milliseconds to wait before hiding tooltip after mouse leaves (default: 0 for instant hide) */
  hideDelay?: number
  /** Allow hovering over the tooltip itself to keep it visible (default: false). When true, default hideDelay becomes 100ms to allow mouse movement to tooltip. */
  interactive?: boolean
  /**
   * CSS selector, evaluated against the trigger's own subtree at hover time.
   * When set, the tooltip only appears if the matched element is currently
   * NOT rendered (display:none / zero client rects) — i.e. the on-screen
   * content it duplicates is hidden. Used by the tab buttons to show the tab
   * name only when its label has collapsed to icon-only (the `.tab-density`
   * container query, or the explicit `iconOnly` prop where no label exists).
   * If nothing matches the selector, the tooltip shows (content is absent).
   * Standard appearance also treats ellipsis as hidden text; span[data-tooltip-text]
   * gets a tab stop only while truncated, measured by the same gate observer.
   */
  showOnlyIfHidden?: string
  /**
   * Also show on keyboard focus inside the trigger and hide on blur (opt-in, std-overlays G18): for a trigger that is a
   * Tab stop whose tooltip carries information a keyboard user needs too (WCAG 1.4.13 / 2.1.1). Escape still dismisses.
   */
  showOnFocus?: boolean
  /**
   * Opt-in keyboard contract (STANDARD 2.10, E6t, std-overlays G19): the tooltip opens when the trigger takes focus and
   * closes on blur (as showOnFocus does), and Escape on the trigger closes the tooltip ONLY (stopped there; passes
   * through when nothing is showing). Off by default: every other caller keeps hover-only.
   */
  openOnFocus?: boolean
  /** Opt-in tap toggle for compact state / truncated text. Existing callers keep their pointer behaviour. */
  openOnTouch?: boolean
  /** Opt in new primitives to the v3 explanation recipe; existing callers keep their adopted tooltip skin. */
  appearance?: 'legacy' | 'standard'

  /** An id for the content; the content then also carries role="tooltip" so the trigger can be described by it. */
  id?: string
  /** A test id on the content. */
  contentTestId?: string
  /** Told when the tooltip shows (true) and hides (false). */
  onOpenChange?: (open: boolean) => void
}

function InstantTooltipImpl({
  content,
  children,
  position = 'top',
  className = '',
  glow = 'white',
  hideDelay,
  interactive = false,
  showOnlyIfHidden,
  showOnFocus = false,
  openOnFocus = false,
  openOnTouch = false,
  appearance = 'legacy',
  id: contentId,
  contentTestId,
  onOpenChange,
  focusable = false
}: InstantTooltipProps) {
  // The content id: the caller's `id` when given (G19: it describes the trigger), else a generated one (focusable chips).
  const generatedId = useId()
  const tooltipId = contentId ?? generatedId
  // Stable unique id for this tooltip instance (survives re-renders)
  const idRef = useRef<number>(-1)
  if (idRef.current === -1) {
    idRef.current = _nextTooltipId++
  }

  // Layer 1: Synchronous visibility ref — prevents stale portals during React's
  // async setState gap. Checked in the render guard alongside isVisible.
  const visibleRef = useRef(false)
  const touchToggleRef = useRef<boolean | null>(null)
  const touchPointerRef = useRef(false)
  const triggerHoveredRef = useRef(false)
  const tooltipHoveredRef = useRef(false)

  // Legacy listeners and gate observers stay lazy (G1). Standard text gates measure on mount for keyboard access.
  const unsubscribeDismissRef = useRef<(() => void) | null>(null)
  const gateObserverRef = useRef<ResizeObserver | null>(null)
  const gateFrameRef = useRef<number | null>(null)
  const dismissLatestRef = useRef<() => void>(() => {})
  const showLatestRef = useRef<() => void>(() => {})
  const gateSelectorRef = useRef<string | undefined>(showOnlyIfHidden)
  gateSelectorRef.current = showOnlyIfHidden
  const truncationGateRef = useRef(appearance === 'standard')
  truncationGateRef.current = appearance === 'standard'

  // Instant hide by default - delayed hiding causes issues with many tooltips
  // Interactive tooltips get a small delay to allow mouse movement to tooltip content
  const effectiveHideDelay = hideDelay ?? (interactive || openOnTouch ? 100 : 0)
  const [isVisible, setIsVisible] = useState(false)
  const [tooltipPosition, setTooltipPosition] = useState<{ top: number; left: number } | null>(null)
  const [isPositioned, setIsPositioned] = useState(false)
  // Owned (inside a migrated modal / the page layer root): render in the owner's slot. No owner: body, lifted above
  // the trigger's own dialog when that dialog is above the tooltip band.
  const owner = useLayerOwner()
  const ownerSlot = owner?.slot ?? null
  const [liftZ, setLiftZ] = useState<number | null>(null)
  const triggerRef = useRef<HTMLSpanElement>(null)
  const tooltipRef = useRef<HTMLDivElement>(null)
  const hideTimeoutRef = useRef<NodeJS.Timeout | null>(null)
  const safetyTimeoutRef = useRef<NodeJS.Timeout | null>(null)

  // Clear any pending hide timeout
  const clearHideTimeout = useCallback(() => {
    if (hideTimeoutRef.current) {
      clearTimeout(hideTimeoutRef.current)
      hideTimeoutRef.current = null
    }
  }, [])

  // Clear safety auto-dismiss timeout
  const clearSafetyTimeout = useCallback(() => {
    if (safetyTimeoutRef.current) {
      clearTimeout(safetyTimeoutRef.current)
      safetyTimeoutRef.current = null
    }
  }, [])

  // Immediate dismiss helper (no delay, no animation)
  const dismissImmediately = useCallback(() => {
    visibleRef.current = false // Layer 1: synchronous guard
    tooltipHoveredRef.current = false
    _unregisterActiveTooltip(idRef.current) // Layer 2: stop tracking
    clearHideTimeout()
    clearSafetyTimeout()
    setIsVisible(false)
    setIsPositioned(false)
    setTooltipPosition(null)
  }, [clearHideTimeout, clearSafetyTimeout])

  // Schedule hiding with delay
  const scheduleHide = useCallback(() => {
    clearHideTimeout()
    if (effectiveHideDelay > 0) {
      hideTimeoutRef.current = setTimeout(() => {
        visibleRef.current = false // Layer 1: synchronous guard
        _unregisterActiveTooltip(idRef.current) // Layer 2: stop tracking
        setIsVisible(false)
        setIsPositioned(false)
        setTooltipPosition(null)
      }, effectiveHideDelay)
    } else {
      visibleRef.current = false // Layer 1: synchronous guard
      _unregisterActiveTooltip(idRef.current) // Layer 2: stop tracking
      setIsVisible(false)
      setIsPositioned(false)
      setTooltipPosition(null)
    }
  }, [effectiveHideDelay, clearHideTimeout])

  // Standard text gates reuse the hidden-label gate for ellipsis and its conditional tab stop.
  const gateIsVisible = useCallback((gate: HTMLElement | null) =>
    !!gate && gate.getClientRects().length > 0 &&
      (!truncationGateRef.current || gate.scrollWidth <= gate.clientWidth), [])

  const reconcileGate = useCallback((allowOpen = true) => {
    const selector = gateSelectorRef.current
    const el = triggerRef.current
    if (!selector || !el) return
    const gate = el.querySelector<HTMLElement>(selector)
    // Only marked static text gets a measured tab stop. Native row controls retain their own focus behaviour.
    if (truncationGateRef.current && gate?.matches('span[data-tooltip-text]')) {
      if (gate.clientWidth > 0 && gate.scrollWidth > gate.clientWidth) gate.setAttribute('tabindex', '0')
      else gate.removeAttribute('tabindex')
    }
    const labelVisible = gateIsVisible(gate)
    const engaged = el.matches(':hover') || el.contains(document.activeElement)
    if (labelVisible) {
      if (visibleRef.current) dismissLatestRef.current()
    } else if (allowOpen && engaged && !visibleRef.current) {
      showLatestRef.current()
    }
  }, [gateIsVisible])

  // Reconcile the `showOnlyIfHidden` gate on layout changes. The legacy gate is only
  // evaluated on mouse-enter/focus, but the gated element's visibility is driven
  // by a CSS container query (`.tab-density`) that can flip while the pointer or
  // focus is already on the trigger (e.g. the panel resizes / split-view toggles).
  // A ResizeObserver on the trigger (its size changes when a label collapses or
  // expands) keeps the tooltip correct in BOTH directions while engaged:
  //   - label becomes visible  → dismiss the now-redundant tooltip
  //   - label becomes hidden   → show it, if the trigger is still hovered/focused
  // It is created on first hover/focus, and its work runs once per animation frame
  // OUTSIDE the observer delivery (measuring inside it is what produced "ResizeObserver
  // loop completed with undelivered notifications").
  const observeGate = useCallback(() => {
    const trigger = triggerRef.current
    if (!trigger || gateObserverRef.current || typeof ResizeObserver === 'undefined') return
    const ro = new ResizeObserver(() => {
      if (gateFrameRef.current === null) gateFrameRef.current = requestAnimationFrame(() => {
        gateFrameRef.current = null
        reconcileGate()
      })
    })
    ro.observe(trigger)
    if (truncationGateRef.current && gateSelectorRef.current) {
      const gate = trigger.querySelector(gateSelectorRef.current)
      if (gate) ro.observe(gate)
    }
    gateObserverRef.current = ro
  }, [reconcileGate])

  // Measure before the first Tab, then on resize or text changes; legacy gates remain lazy.
  useLayoutEffect(() => {
    if (appearance !== 'standard' || !showOnlyIfHidden) return
    observeGate()
    reconcileGate(false)
  }, [appearance, showOnlyIfHidden, children, content, observeGate, reconcileGate])

  useEffect(() => {
    if (appearance !== 'standard' || !showOnlyIfHidden) return
    const gate = triggerRef.current?.querySelector<HTMLElement>(showOnlyIfHidden)
    if (!gate?.matches('span[data-tooltip-text]')) return
    if (isVisible) gate.setAttribute('aria-describedby', tooltipId)
    else if (gate.getAttribute('aria-describedby') === tooltipId) gate.removeAttribute('aria-describedby')
  }, [appearance, showOnlyIfHidden, isVisible, tooltipId, children])

  // Show tooltip immediately, dismissing all other tooltips first
  const showTooltip = useCallback(() => {
    if (appearance === 'standard' && !content) return
    ensureScrollListener()
    _ensureSafetyValve()
    if (!unsubscribeDismissRef.current) {
      // Listen for dismiss broadcasts from other tooltip instances (or scroll events)
      unsubscribeDismissRef.current = subscribeDismiss((senderId) => {
        // senderId === -1 means "dismiss all" (from scroll handler)
        if (senderId !== idRef.current) dismissLatestRef.current()
      })
    }
    if (!gateObserverRef.current && gateSelectorRef.current && typeof ResizeObserver !== 'undefined') {
      observeGate()
    }
    // Conditional suppression: when `showOnlyIfHidden` is set, only show the
    // tooltip if the matched element inside the trigger is currently hidden.
    // A tab label collapsed to icon-only (via the .tab-density container query)
    // renders with display:none → no client rects → tooltip shows. When the
    // label is on screen, the tooltip would just repeat it, so skip it. Checked
    // at hover time so it always reflects the live responsive state. (No match
    // means the content is absent — e.g. explicit iconOnly mode — so show it.)
    if (showOnlyIfHidden && triggerRef.current) {
      const gate = triggerRef.current.querySelector(showOnlyIfHidden) as HTMLElement | null
      if (gateIsVisible(gate)) {
        return
      }
    }
    // Dismiss every other visible tooltip before opening this one
    broadcastDismiss(idRef.current)
    setLiftZ(ownerSlot ? null : tooltipZAbove(triggerRef.current))
    clearHideTimeout()
    clearSafetyTimeout()
    visibleRef.current = true // Layer 1: synchronous guard
    setIsVisible(true)

    // Layer 2: Register in active tooltips map for orphan detection
    _registerActiveTooltip(idRef.current, {
      dismiss: () => {
        visibleRef.current = false
        _unregisterActiveTooltip(idRef.current)
        clearHideTimeout()
        clearSafetyTimeout()
        setIsVisible(false)
        setIsPositioned(false)
        setTooltipPosition(null)
      },
      triggerRef,
    })

    // Safety net: auto-dismiss interactive tooltips after max lifetime.
    // This prevents "stuck" tooltips if all other dismiss mechanisms fail.
    if (interactive && !openOnTouch) {
      safetyTimeoutRef.current = setTimeout(() => {
        if (process.env.NODE_ENV !== 'production') {
          console.debug('[Tooltip] Safety auto-dismiss after %dms (id=%d)', INTERACTIVE_TOOLTIP_MAX_LIFETIME_MS, idRef.current)
        }
        visibleRef.current = false
        _unregisterActiveTooltip(idRef.current)
        setIsVisible(false)
        setIsPositioned(false)
        setTooltipPosition(null)
      }, INTERACTIVE_TOOLTIP_MAX_LIFETIME_MS)
    }
  }, [appearance, content, clearHideTimeout, clearSafetyTimeout, interactive, openOnTouch, showOnlyIfHidden, ownerSlot, observeGate, gateIsVisible])

  dismissLatestRef.current = dismissImmediately
  showLatestRef.current = showTooltip

  // A menu can temporarily suppress its trigger's explanation without replacing the trigger or its ref.
  useEffect(() => {
    if (appearance === 'standard' && !content && visibleRef.current) dismissImmediately()
  }, [appearance, content, dismissImmediately])

  // Clean up all timeouts and active tooltip registration on unmount
  useEffect(() => {
    const tooltipId = idRef.current
    return () => {
      clearHideTimeout()
      clearSafetyTimeout()
      _unregisterActiveTooltip(tooltipId)
      visibleRef.current = false
      unsubscribeDismissRef.current?.()
      unsubscribeDismissRef.current = null
      if (gateFrameRef.current !== null) {
        cancelAnimationFrame(gateFrameRef.current)
        gateFrameRef.current = null
      }
      gateObserverRef.current?.disconnect()
      gateObserverRef.current = null
    }
  }, [clearHideTimeout, clearSafetyTimeout])

  // Dismiss on Escape key press (works for all tooltips, not just interactive)
  useEffect(() => {
    if (!isVisible || (appearance === 'standard' && !content)) return
    // New touch-capable explanations join the existing dismissal stack. Legacy callers retain their behaviour.
    if (openOnTouch) return pushLayer({
      id: tooltipId,
      modal: false,
      get trigger() { return triggerRef.current },
      get content() { return tooltipRef.current },
      close: dismissImmediately,
    })

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        dismissImmediately()
      }
    }

    document.addEventListener('keydown', handleKeyDown)
    return () => {
      document.removeEventListener('keydown', handleKeyDown)
    }
  }, [appearance, content, isVisible, openOnTouch, tooltipId, dismissImmediately])

  // Dismiss interactive tooltips on any click outside the tooltip and its trigger.
  // This prevents the tooltip (z-[9999], pointer-events: auto) from blocking clicks
  // on distant UI elements like the End Call button when the tooltip content
  // physically overlaps them via portal positioning.
  useEffect(() => {
    if (!(interactive || openOnTouch) || !isVisible) return

    const handlePointerDown = (e: PointerEvent) => {
      const target = e.target as Node
      // Keep tooltip open if clicking inside the tooltip or trigger
      if (tooltipRef.current?.contains(target)) return
      if (triggerRef.current?.contains(target)) return
      // Dismiss immediately
      dismissImmediately()
    }

    // Use capture phase so the tooltip is dismissed BEFORE the click reaches
    // the target element, preventing the tooltip from swallowing the event.
    document.addEventListener('pointerdown', handlePointerDown, true)
    return () => {
      document.removeEventListener('pointerdown', handlePointerDown, true)
    }
  }, [interactive, openOnTouch, isVisible, dismissImmediately])

  // WCAG 4.1.2 (Q-2214): an icon-only control wrapped by a tooltip has no accessible
  // name. Give the tooltip text to any interactive child that has none (no text,
  // aria-label, aria-labelledby, title or labelled image). A control that is
  // already named is never touched, and only attributes added here are removed.
  // The child can also change on its own (its own state), so a MutationObserver
  // re-checks between our renders.
  const nameOwnerToken = useRef({})
  // A new (equivalent) child element on every parent render must not rebuild the observer,
  // but the names are re-checked on every render of the tooltip: one cheap pass, no allocation.
  useEffect(() => {
    const root = triggerRef.current
    if (!root || typeof content !== 'string' || !content) return
    syncTooltipNames(root, content, nameOwnerToken.current)
  }, [content, children])
  useEffect(() => {
    const root = triggerRef.current
    if (!root || typeof content !== 'string' || !content) return
    const owner = nameOwnerToken.current
    syncTooltipNames(root, content, owner)
    const MO = root.ownerDocument.defaultView?.MutationObserver
    const observer = MO ? new MO(() => syncTooltipNames(root, content, owner)) : null
    observer?.observe(root, NAME_OBSERVE_OPTIONS)
    return () => {
      observer?.disconnect()
      root.querySelectorAll(`[${TOOLTIP_NAME_MARK}]`).forEach(el => {
        if ((tooltipNameOwner.get(el) ?? owner) === owner) releaseTooltipName(el)
      })
    }
  }, [content])

  // onOpenChange (opt-in): told on every change after mount, not for the initial closed state.
  const onOpenChangeRef = useRef(onOpenChange)
  onOpenChangeRef.current = onOpenChange
  const reportedOpenRef = useRef(false)
  useEffect(() => {
    if (reportedOpenRef.current === isVisible) return
    reportedOpenRef.current = isVisible
    onOpenChangeRef.current?.(isVisible)
  }, [isVisible])

  // Escape on a focus-opened tooltip's trigger closes the tooltip only (openOnFocus).
  const handleTriggerKeyDown = useCallback((e: React.KeyboardEvent) => {
    if (e.key !== 'Escape' || !visibleRef.current) return
    e.stopPropagation()
    dismissImmediately()
  }, [dismissImmediately])

  // Capture the intended toggle before a touch-induced focus or compatibility mouse event can open it.
  const handleTouchPointerDown = (event: React.PointerEvent) => {
    touchPointerRef.current = event.pointerType === 'touch'
    touchToggleRef.current = touchPointerRef.current ? !visibleRef.current : null
  }
  const handleTouchClick = (event: React.MouseEvent) => {
    const next = touchToggleRef.current
    touchToggleRef.current = null
    if (next === null && (event.nativeEvent as PointerEvent).pointerType !== 'touch') return
    if (next ?? !visibleRef.current) showTooltip()
    else dismissImmediately()
  }
  const hideUnlessFocused = () => {
    if (openOnTouch && (triggerHoveredRef.current || tooltipHoveredRef.current || triggerRef.current?.contains(document.activeElement))) return
    scheduleHide()
  }

  // Use useLayoutEffect for synchronous position calculation before browser paint
  // This prevents the flash at (0,0) before moving to correct position
  useLayoutEffect(() => {
    if (isVisible && triggerRef.current && tooltipRef.current) {
      const triggerRect = triggerRef.current.getBoundingClientRect()
      // The layout size, not the painted box: until it is positioned the tooltip is drawn at scale-95, and a box measured
      // through that transform is 5% short, so the viewport clamp below let it hang past the edge once it grew to full
      // size. (happy-dom has no layout: fall back to the rect there.)
      const measured = tooltipRef.current.getBoundingClientRect()
      const tooltipRect = {
        width: tooltipRef.current.offsetWidth || measured.width,
        height: tooltipRef.current.offsetHeight || measured.height,
      }
      const padding = 8

      let top = 0
      let left = 0

      switch (position) {
        case 'top':
          top = triggerRect.top - tooltipRect.height - padding
          left = triggerRect.left + (triggerRect.width / 2) - (tooltipRect.width / 2)
          break
        case 'bottom':
          top = triggerRect.bottom + padding
          left = triggerRect.left + (triggerRect.width / 2) - (tooltipRect.width / 2)
          break
        case 'left':
          top = triggerRect.top + (triggerRect.height / 2) - (tooltipRect.height / 2)
          left = triggerRect.left - tooltipRect.width - padding
          break
        case 'right':
          top = triggerRect.top + (triggerRect.height / 2) - (tooltipRect.height / 2)
          left = triggerRect.right + padding
          break
      }

      // Keep tooltip within viewport
      const viewportWidth = window.innerWidth
      const viewportHeight = window.innerHeight

      if (left < padding) left = padding
      if (left + tooltipRect.width > viewportWidth - padding) {
        left = viewportWidth - tooltipRect.width - padding
      }
      if (top < padding) top = padding
      if (top + tooltipRect.height > viewportHeight - padding) {
        top = viewportHeight - tooltipRect.height - padding
      }

      setTooltipPosition({ top, left })
      // Mark as positioned AFTER setting position - this triggers the fade-in
      setIsPositioned(true)
    }
  }, [isVisible, position])

  // Tooltip content - only render when visible, start invisible until positioned
  // This prevents the flash at (0,0) that occurs before position calculation
  // Skin: named tokens (styles/design-tokens.css section 10, ADOPT-AS-TOKEN of the values that were inline here):
  // surface, edge, ink, blur, and the two glow strengths (shadow + drop-shadow).
  const glowClasses = glow === 'white'
    ? 'shadow-tooltip-strong drop-shadow-tooltip-strong'
    : 'shadow-tooltip drop-shadow-tooltip'

  // Transform origin should point toward the trigger element so scale animation
  // expands outward from the hover point, not from default center
  const transformOriginMap: Record<string, string> = {
    top: 'bottom center',
    bottom: 'top center',
    left: 'center right',
    right: 'center left',
  }

  // Layer 1 render guard: visibleRef is set synchronously — if dismiss was
  // called but React hasn't re-rendered yet, the ref prevents stale portals.
  if (isVisible && !visibleRef.current) {
    if (process.env.NODE_ENV !== 'production') {
      console.debug('[Tooltip] visibleRef guard prevented stale render (id=%d)', idRef.current)
    }
  }

  const tooltipContent = isVisible && visibleRef.current && content && (
    <div
      ref={tooltipRef}
      id={tooltipId}
      role="tooltip"
      data-instant-tooltip-scroll-portal={interactive ? 'true' : undefined}
      className={`fixed z-[10100] ${appearance === 'standard' ? `
        px-2 py-1 text-meta
        border border-edge rounded-float
        bg-popup text-ink shadow-float backdrop-blur-lg
        max-w-[min(20rem,calc(100vw-1rem))] whitespace-pre-wrap
        ease-out
        ${isPositioned ? 'opacity-100 scale-100' : 'opacity-0 scale-95'}
      ` : `
        px-3 py-2 text-sm
        border border-tooltip-edge rounded-lg
        bg-tooltip text-tooltip-ink backdrop-blur-lg ${glowClasses}
        max-w-xs whitespace-pre-wrap
        ease-out
        ${isPositioned ? 'opacity-100 scale-100' : 'opacity-0 scale-95'}
      `}`}
      style={{
        // Use -9999 as initial position until positioned, then use calculated position
        top: tooltipPosition?.top ?? -9999,
        left: tooltipPosition?.left ?? -9999,
        pointerEvents: interactive || openOnTouch ? 'auto' : 'none',
        ...(liftZ !== null ? { zIndex: liftZ } : null),
        // Only transition opacity and transform - NOT top/left which would cause
        // the tooltip to visibly fly from -9999px to its position
        transition: 'opacity 150ms ease-out, transform 150ms ease-out',
        transformOrigin: transformOriginMap[position] || 'bottom center',
      }}
      onMouseEnter={openOnTouch ? () => { tooltipHoveredRef.current = true; showTooltip() } : interactive ? showTooltip : undefined}
      onMouseLeave={openOnTouch ? () => { tooltipHoveredRef.current = false; hideUnlessFocused() } : interactive ? scheduleHide : undefined}
      {...(contentTestId ? { 'data-testid': contentTestId } : null)}
    >
      {content}
    </div>
  )

  return (
    <>
      <span
        ref={triggerRef}
        tabIndex={focusable ? 0 : undefined}
        aria-describedby={focusable && isVisible ? tooltipId : undefined}
        onMouseEnter={openOnTouch ? () => { if (!touchPointerRef.current) { triggerHoveredRef.current = true; showTooltip() } } : showTooltip}
        onMouseLeave={openOnTouch ? () => { triggerHoveredRef.current = false; hideUnlessFocused() } : scheduleHide}
        onPointerEnter={openOnTouch ? event => { touchPointerRef.current = event.pointerType === 'touch' } : undefined}
        onPointerDown={openOnTouch ? handleTouchPointerDown : undefined}
        onPointerCancel={openOnTouch ? () => { touchToggleRef.current = null } : undefined}
        onClick={openOnTouch ? handleTouchClick : undefined}
        // Explicit chip triggers (`focusable`) and gated tab labels expose their explanation to keyboard users; `showOnFocus` /
        // `openOnFocus` opt an ungated tooltip in (the trigger is a Tab stop and the tooltip is information); `openOnFocus` also
        // gives the full keyboard contract (Escape closes the tooltip only). Ordinary callers retain their hover-only model.
        onFocus={focusable || showOnlyIfHidden || showOnFocus || openOnFocus ? () => {
          if (!openOnTouch || touchToggleRef.current === null) showTooltip()
        } : undefined}
        onBlur={focusable || showOnlyIfHidden || showOnFocus || openOnFocus ? (openOnTouch ? hideUnlessFocused : scheduleHide) : undefined}
        onKeyDown={openOnFocus ? handleTriggerKeyDown : undefined}
        onClickCapture={interactive || openOnTouch ? undefined : dismissImmediately}
        className={`inline ${focusable ? "rounded-control focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-transparent focus-visible:ring-2 focus-visible:ring-edge-focus" : ""} ${className}`}
      >
        {children}
      </span>
      {tooltipContent && typeof document !== 'undefined' && createPortal(tooltipContent, ownerSlot ?? document.body)}
    </>
  )
}

// Memoised: ~100 of these sit in the officer's queue; an unchanged parent render must not render them.
export const InstantTooltip = memo(InstantTooltipImpl)
